import type {
  ImajiProStatus,
  OrderPaymentIssue,
  OrderStatus,
  PaymentChannel,
  PaymentKind,
  PaymentStatus,
} from '@/lib/api';

export { formatRupiah } from '../product/labels';

export const STATUS_LABEL: Record<OrderStatus, string> = {
  DRAFT: 'Draf',
  PENDING_PAYMENT: 'Menunggu bayar',
  PAID: 'Dibayar',
  IN_REVIEW: 'Menunggu persetujuan desain',
  IN_PRODUCTION: 'Diproduksi',
  SHIPPED: 'Dikirim',
  COMPLETED: 'Selesai',
  CANCELLED: 'Dibatalkan',
};

/** Urutan chip filter: alur normal dulu, status akhir di belakang. */
export const STATUS_ORDER: OrderStatus[] = [
  'PENDING_PAYMENT',
  'PAID',
  'IN_REVIEW',
  'IN_PRODUCTION',
  'SHIPPED',
  'COMPLETED',
  'CANCELLED',
];

/**
 * Warna badge menandai siapa yang sedang ditunggu: lime berarti giliran CI,
 * redup berarti menunggu pelanggan atau sudah selesai.
 */
export const STATUS_TONE: Record<OrderStatus, string> = {
  DRAFT: 'bg-charcoal/10 text-charcoal/75',
  PENDING_PAYMENT: 'bg-charcoal/10 text-charcoal/75',
  PAID: 'bg-charcoal text-lime',
  IN_REVIEW: 'bg-charcoal text-lime',
  IN_PRODUCTION: 'bg-cobalt text-clickwhite',
  SHIPPED: 'bg-cobalt/15 text-cobalt',
  COMPLETED: 'bg-charcoal/10 text-charcoal/75',
  CANCELLED: 'bg-redline/10 text-redline',
};

/** Kata kerja untuk tombol aksi; berbeda dari label status yang berupa keadaan. */
export const TRANSITION_ACTION: Record<OrderStatus, string> = {
  DRAFT: 'Jadikan draf',
  PENDING_PAYMENT: 'Tunggu pembayaran',
  PAID: 'Tandai dibayar',
  IN_REVIEW: 'Minta persetujuan desain',
  IN_PRODUCTION: 'Mulai produksi',
  SHIPPED: 'Tandai dikirim',
  COMPLETED: 'Tandai selesai',
  CANCELLED: 'Batalkan pesanan',
};

export const PAYMENT_CHANNEL_LABEL: Record<PaymentChannel, string> = {
  QRIS: 'QRIS',
  VA_BCA: 'Virtual Account BCA',
  BANK_TRANSFER: 'Transfer bank',
};

export { LISTENER_SOURCE_LABEL } from '@ciptaimaji/shared';

export const PAYMENT_KIND_LABEL: Record<PaymentKind, string> = {
  FULL: 'Bayar penuh',
  DOWN_PAYMENT: 'DP',
  SETTLEMENT: 'Pelunasan',
};

export const PAYMENT_STATUS_LABEL: Record<PaymentStatus, string> = {
  PENDING: 'Menunggu',
  PAID: 'Lunas',
  EXPIRED: 'Kedaluwarsa',
  CANCELED: 'Dibatalkan',
  FAILED: 'Gagal',
};

export const PAYMENT_ISSUE_LABEL: Record<OrderPaymentIssue, string> = {
  OVERPAID: 'Kelebihan bayar',
  PAID_ON_CANCELLED: 'Bayar setelah batal',
  UNCONFIRMED: 'Status bayar belum pasti',
};

export const PAYMENT_ISSUE_HINT: Record<OrderPaymentIssue, string> = {
  OVERPAID:
    'Uang masuk melebihi total pesanan, biasanya karena dua tagihan sama-sama dibayar. Cocokkan di dashboard Komerce lalu kembalikan kelebihannya.',
  PAID_ON_CANCELLED:
    'Ada pembayaran masuk padahal pesanan sudah dibatalkan. Pesanan tidak dihidupkan otomatis; hubungi pelanggan untuk memproses ulang atau mengembalikan dana.',
  UNCONFIRMED:
    'Tagihan sudah lewat waktu hangus tetapi kabar dari Komerce tidak pernah masuk. Tekan "Cek ke Komerce" pada tagihan di bawah untuk memastikan.',
};

export const IMAJIPRO_LABEL: Record<ImajiProStatus, string> = {
  PENDING: 'Menunggu kirim',
  SENT: 'Terkirim',
  FAILED: 'Gagal kirim',
};

export const IMAJIPRO_TONE: Record<ImajiProStatus, string> = {
  PENDING: 'bg-charcoal/8 text-charcoal/80',
  SENT: 'bg-lime/60 text-charcoal',
  FAILED: 'bg-redline/10 text-redline',
};

/**
 * Status produksi datang mentah dari ImajiPRO (string bebas, pemiliknya di sana),
 * jadi hanya yang sudah dikenal yang diterjemahkan; sisanya dirapikan apa adanya
 * supaya status baru di ImajiPRO tetap kelihatan di panel tanpa menunggu rilis.
 */
const PRODUCTION_LABEL: Record<string, string> = {
  draft: 'Draf',
  pending: 'Menunggu',
  waiting: 'Menunggu',
  queued: 'Antre',
  confirmed: 'Dikonfirmasi',
  in_progress: 'Dikerjakan',
  processing: 'Dikerjakan',
  production: 'Produksi',
  ready: 'Siap',
  done: 'Selesai',
  completed: 'Selesai',
  finished: 'Selesai',
  delivered: 'Diserahkan',
  cancelled: 'Dibatalkan',
  canceled: 'Dibatalkan',
  on_hold: 'Ditahan',
  hold: 'Ditahan',
};

export function productionStatusLabel(status: string): string {
  const key = status
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
  const known = PRODUCTION_LABEL[key];
  if (known) return known;
  const text = status.trim().replace(/[_-]+/g, ' ');
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** Selesai/dibatalkan sudah final, sisanya sedang berjalan. */
export function productionTone(status: string): string {
  const key = status
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
  if (['done', 'completed', 'finished', 'delivered', 'ready'].includes(key)) {
    return 'bg-lime/60 text-charcoal';
  }
  if (['cancelled', 'canceled', 'on_hold', 'hold'].includes(key))
    return 'bg-redline/10 text-redline';
  return 'bg-cobalt/10 text-cobalt';
}

export function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('id-ID', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('id-ID', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}
